import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { UrlTree } from '@angular/router';
import { Observable, of, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, mergeMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { IsAccountPremiumGuard } from './base/is-account-premium.guard';
import { IsAccountValidGuard } from './base/is-account-valid.guard';
import { LoadDomainIfExistsGuard } from './base/load-domain-if-exists.guard';
import { LoadFreemiumViewerThemeGuard } from './base/load-freemium-viewer-theme.guard';
import { LoadServicesGuard } from './base/load-services.guard';
import * as fromRoot from '@app/reducers';
import { Store, select } from '@ngrx/store';
import { AccountActions } from '@app/core/actions';
import { CookiesService } from '../services/cookies.service';
import { isPlatformServer } from '@angular/common';
import { SEOService } from '../services/seo.service';
import { ViewerActions } from '@app/client/viewer/actions';
import * as fromViewer from '@app/client/viewer/reducers/viewer.reducer';
import { MacroTaskWrapperService } from '../services/MacroTaskWrapper.service';
import { LoadRolesAndTeamsGuard } from './base/load-roles-and-teams.guard';
@Injectable({
  providedIn: 'root'
})
export class ViewerGuard {
  constructor(
    private store$: Store<fromRoot.State & fromViewer.State>,
    private loadDomainGuardIfExists: LoadDomainIfExistsGuard,
    private loadServicesGuard: LoadServicesGuard,
    private isAccountValidGuard: IsAccountValidGuard,
    private isAccountPremiumGuard: IsAccountPremiumGuard,
    private loadFreemiumViewerThemeGuard: LoadFreemiumViewerThemeGuard,
    private loadTeamsGuard: LoadRolesAndTeamsGuard,
    private cookiesService: CookiesService,
    private macroTaskWrapper: MacroTaskWrapperService,
    private seoService: SEOService,
    @Inject(PLATFORM_ID) private platformId: any
  ) { }

  canActivate(): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    if (isPlatformServer(this.platformId)) {
      // this delegate the execution of the guards on the browser side, hence meta won't be generated
      // return null; // by pass the guard delegate to the component on the client side
      return this.loadTransferInternal().pipe(mergeMap((transferInternalLoaded: boolean) => {
        if (transferInternalLoaded) {
          return of(transferInternalLoaded);
        }
      }));
    } else {
      return this.loadDomainGuardIfExists.canActivate().pipe(mergeMap((domainLoaded: boolean) => {
        if (domainLoaded) {
          return this.loadServicesGuard.canActivate().pipe(mergeMap((servicesLoaded: boolean) => {
            if (servicesLoaded) {
              return this.isAccountValidGuard.canActivate().pipe(mergeMap((isAccountValid: boolean) => {
                if (isAccountValid) {
                  return this.isAccountPremiumGuard.canActivate().pipe(mergeMap((isLoggedIn: boolean) => {
                    if (isLoggedIn) {
                      return this.loadTeamsGuard.canActivate().pipe(mergeMap((teamsLoaded: boolean) => {
                        if (teamsLoaded) {
                          return this.checkTeamLoaded().pipe(mergeMap((ok: boolean) => {
                            return this.loadFreemiumViewerThemeGuard.canActivate().pipe(mergeMap((themeLoaded: boolean) => {
                              if (themeLoaded) {
                                return of(themeLoaded);
                              }
                            }));
                          }));
                        }
                      }));
                    } else {
                      return this.loadFreemiumViewerThemeGuard.canActivate().pipe(mergeMap((themeLoaded: boolean) => {
                        if (themeLoaded) {
                          return of(themeLoaded);
                        }
                      }));
                    }
                  }));
                }
              }));
            }
          }));
        }
      }));
    }

  }


  loadTransferInternal(): Observable<boolean> {
    const transferLoaded$ = new ReplaySubject<boolean>();
    this.store$.dispatch(ViewerActions.LoadTransferMetas());
    this.store$
      .pipe(
        withLatestFrom(
          this.store$.select(fromViewer.getTransferMetas),
          this.store$.select(fromViewer.getTransferMetasError),
        ),
        distinctUntilChanged(),
        takeUntil(transferLoaded$),
      )
      .subscribe(([store, transferMetas, error]) => {
        this.macroTaskWrapper.awaitMacroTasks("transferSdk.getTransferInternal");
        this.macroTaskWrapper.awaitMacroTasks("linkSdk.getTarget");
        if (transferMetas) {
          this.seoService.setMetasViewer(transferMetas);
          transferLoaded$.next(true);
        } else if (error) {
          transferLoaded$.next(true);
        }

      });
    return transferLoaded$.asObservable();
  }

  checkTeamLoaded(): Observable<boolean> {
    const teamLoaded$ = new ReplaySubject<boolean>();
    this.store$.pipe(
      withLatestFrom(
        this.store$.select(fromRoot.getUserTeams),
      ),
      distinctUntilChanged(),
      takeUntil(teamLoaded$),
    ).subscribe(([store, teams]: any) => {
      const lastActiveTeam = this.cookiesService.getCookie('lastActiveTeam');
      const resolvedTeam = teams.find(team => lastActiveTeam?.team === team?.id);
      this.store$.dispatch(AccountActions.updateActiveTeam({ team: resolvedTeam || teams[0] }));
      teamLoaded$.next(true);
    });

    return teamLoaded$.asObservable();
  }
}
